import type { RemittanceDetail } from '@erp/rpc';

type LineItem = RemittanceDetail['lineItems'][number];

export interface LineItemActions {
  /** The invoice Confirm would lock in; null when Confirm isn't offered. */
  confirmInvoiceId: string | null;
  reject: boolean;
  undo: boolean;
}

/**
 * Which resolve actions a line offers. A confirmed (`matched`) line offers
 * none; a rejected one only Undo; any other line offers Reject, plus Confirm
 * once it has an invoice to confirm.
 */
export function getLineItemActions(
  lineItem: Pick<LineItem, 'matchStatus' | 'invoiceId'>,
): LineItemActions {
  const status = lineItem.matchStatus;
  const isOpen = status !== 'matched' && status !== 'rejected';
  return {
    confirmInvoiceId: isOpen ? lineItem.invoiceId : null,
    reject: isOpen,
    undo: status === 'rejected',
  };
}

export function hasLineItemActions(actions: LineItemActions): boolean {
  return actions.confirmInvoiceId != null || actions.reject || actions.undo;
}

/** A stated amount that can be saved: a finite, non-zero number. */
export function isSubmittableAmount(amount: string | null): boolean {
  if (amount === null) return false;
  const value = Number(amount);
  return Number.isFinite(value) && value !== 0;
}

/** The new-line row can be added once an invoice and a usable amount are set. */
export function canAddLineItem(
  invoiceId: string | null,
  amount: string | null,
  pending: boolean,
): boolean {
  return invoiceId !== null && isSubmittableAmount(amount) && !pending;
}

/** The picked invoice's customer as the ERP shows it, else the raw name. */
export function invoiceCustomerLabel(
  invoice: {
    erpDisplayName: string | null;
    customerName: string | null;
  } | null,
): string {
  return invoice?.erpDisplayName ?? invoice?.customerName ?? '--';
}
